import { useEffect, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { fg } from '@atlaskit/platform-feature-flags';
import { RequestStatus, useBFFDataProvider } from '@atlassian/navigation-bff-data-provider';

import type { GlobalShortcutsItem, ShortcutsConfig } from '../../types';
import { productLinksWorkspaceCloudIdFilter } from '../product-link-filters';

import type { InferShortcutsOutput, UseGlobalShortcutDataInputBase } from './types';

/**
 * @param input.shortcutsConfig - Configuration for global shortcuts. Accepts either a `ShortcutsConfig` record or a `GlobalShortcutsItem[]` array; the return type matches the input shape.
 * @returns Shortcuts config with URLs hydrated from the BFF data provider.
 */
export const useGlobalShortcutsData = <
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	shortcutsConfig,
	cloudId,
	product,
	orgId,
	isShortcutsStatic,
	onError,
}: UseGlobalShortcutDataInputBase & { shortcutsConfig: T }): InferShortcutsOutput<T> => {
	const { buildURLAndFetchData, status, productLinks, error } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		forceStatic: isShortcutsStatic,
		product,
	});

	// fetch the data from the bff if required
	useEffect(() => {
		if (
			isEmpty(shortcutsConfig) ||
			isShortcutsStatic ||
			(!cloudId && fg('navx-4176-useglobalshortcutsdata-exit-early'))
		) {
			return;
		}
		buildURLAndFetchData();
	}, [buildURLAndFetchData, cloudId, shortcutsConfig, isShortcutsStatic]);

	useEffect(() => {
		if (status === RequestStatus.ERROR && error) {
			onError?.(error);
		}
	}, [status, error, onError]);

	// associate shortcuts with the returned bff data
	const hydratedAppShortcuts = useMemo(() => {
		if (isEmpty(shortcutsConfig) && fg('navx-4176-useglobalshortcutsdata-exit-early')) {
			return shortcutsConfig;
		}

		if (status === RequestStatus.COMPLETE && Object.keys(productLinks).length) {
			const isArray = Array.isArray(shortcutsConfig);

			if (isArray) {
				return shortcutsConfig.reduce<GlobalShortcutsItem[]>((acc, item) => {
					// if config does not need a data reference, just return the value
					if (item.type !== 'link' || !item.apsKey) {
						acc.push(item);
						return acc;
					}

					// if config requires the bff to determine the data reference
					const productLink = productLinks[item.apsKey];
					if (productLink) {
						const filteredProductLink = cloudId
							? productLink.filter(productLinksWorkspaceCloudIdFilter(cloudId))[0]
							: productLink[0];
						acc.push({
							...item,
							url: filteredProductLink.url,
						});
					}
					return acc;
				}, []);
			}

			const entries = Object.entries(shortcutsConfig || {});
			return entries.reduce<ShortcutsConfig>((acc, [key, value]) => {
				// if config does not need a data reference, just return the value
				if (value.type !== 'link' || !value.apsKey) {
					acc[key] = value;
					return acc;
				}

				// if config requires the bff to determine the data reference
				const productLink = productLinks[value.apsKey];
				if (productLink) {
					const filteredProductLink = cloudId
						? productLink.filter(productLinksWorkspaceCloudIdFilter(cloudId))[0]
						: productLink[0];
					acc[key] = {
						...value,
						url: filteredProductLink.url,
					};
					return acc;
				}
				return acc;
			}, {});
		}

		return shortcutsConfig;
	}, [productLinks, status, shortcutsConfig, cloudId]);

	return hydratedAppShortcuts as InferShortcutsOutput<T>;
};
